import {toDecimalFr} from 'ff-common';
import {TFunction} from 'i18next';
import type {
  ContactInfo,
  MatchingDetailsLigne,
  StatutProcessusPdf,
} from 'api-types/ffconnect';

// Couleurs associées à chaque statut de processus PDF
export const processusStatutColors: Record<
  StatutProcessusPdf,
  {color: string; bg: string}
> = {
  commande_validee: {color: 'green.success', bg: 'green.success_bg'},
  panier: {color: 'blue.main', bg: 'blue.bg'},
  erreur: {color: 'red.fail', bg: 'red.warning_bg'},
  en_cours: {color: 'blue.main', bg: 'blue.bg'},
  archive: {color: 'gray.dark', bg: 'gray.200'},
};

export const formatFileSize = (bytes: number, t: TFunction): string => {
  if (bytes === 0) return t('file_size.zero_bytes');
  const k = 1024;
  const sizeKeys = ['bytes', 'kb', 'mb', 'gb'] as const;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const sizeKey = sizeKeys[i] || 'bytes';
  const size = toDecimalFr(Math.round((bytes / Math.pow(k, i)) * 100) / 100, 2);
  return `${size} ${t(`file_size.${sizeKey}`)}`;
};

export const isValidPdfFile = (file: File): boolean => {
  return file.type === 'application/pdf';
};

// Formate les informations d'un contact en un string lisible
// Utilisé dans le rapport extract des processus PDF.
export const formatContactInfo = (
  contact: ContactInfo | undefined,
): string | null => {
  if (
    !contact ||
    (!contact.nom &&
      !contact.prenom &&
      !contact.email &&
      !contact.telephone &&
      !contact.fonction)
  ) {
    return null;
  }

  const parts: string[] = [];
  if (contact.nom || contact.prenom) {
    parts.push([contact.prenom, contact.nom].filter(Boolean).join(' '));
  }
  if (contact.fonction) {
    parts.push(`(${contact.fonction})`);
  }
  if (contact.email) {
    parts.push(contact.email);
  }
  if (contact.telephone) {
    parts.push(contact.telephone);
  }

  return parts.length > 0 ? parts.join(' - ') : null;
};

// Import PDF : Le montant d'une ligne du bon de commande (sa quantité x son prix unitaire)
// a-t-il pu être comparé au prix catalogue ? Le rapport le dit par
// `est_comparable` depuis l'ajout du champ. Pour les rapports antérieurs, qui ne
// le portent pas, on le déduit des autres champs : un écart nul alors que les
// prix ne sont pas déclarés identiques ne peut pas sortir d'une comparaison
// réelle — c'est la trace d'une comparaison que le serveur n'a pas faite.
// Réponse commune à la phrase de contrôle du panier et aux deux tables de
// comparaison, pour qu'elles ne se contredisent pas sur une même ligne.
export const prixLigneAEteCompare = (
  prix: MatchingDetailsLigne['prix_ligne_total'],
): boolean => {
  if (!prix?.etat) return false;
  if (prix.est_comparable !== undefined) return prix.est_comparable;
  return !((prix.ecart_pourcentage ?? 0) === 0 && !prix.identique_calcule);
};
